// src/voip/VoipProvider.jsx

import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { getLoggedInUser } from '../selectors/userSelectors';
import { getCurrentOrg } from '../selectors/organizationSelectors';
import useIsVoipUser from '../hooks/useIsVoipUser';
import { sipClient } from './sipClient';
import { useGetTurnServersMutation } from '../services/VoipService';
import { nativeBridgeClient } from './nativeBridgeClient';
import { normalizePhone } from '../helpers/PhoneHelpers';
import { isMobile } from '../helpers/BrowserHelpers';

const VoipContext = createContext(null);
export const useVoip = () => useContext(VoipContext);

export const VoipProvider = ({ children }) => {
  const currentUser = useSelector(getLoggedInUser);
  // Use the actively-selected org (state.auth.currentOrg) — for multi-org
  // users with the same email, user.organization can lag behind the
  // explicit switch made via /changeOrg, which would leave VoIP on the
  // initially-loaded org's domain.
  const organization = useSelector(getCurrentOrg);
  const [getTurnServers] = useGetTurnServersMutation();
  const isNativeApp = useSelector((state) => state.nativeApp?.isNativeApp);
  const engine = isNativeApp
    ? nativeBridgeClient
    : sipClient;

  const [dialedNumber, setDialedNumber] = useState('');
  const [clientReady, setClientReady] = useState(false);
  const [incomingCall, setIncomingCall] = useState(null);
  const [activeCall, setActiveCall] = useState(null);
  // Call waiting: the established call parked while a second incoming call is answered.
  const [heldCall, setHeldCall] = useState(null);
  // Contact for a call ringing while another call is in progress; kept apart from callContact
  // so the waiting banner never replaces the active call's panel.
  const [incomingContact, setIncomingContact] = useState(null);
  // Contact of the parked call, captured when it is parked / swapped out.
  const [heldContact, setHeldContact] = useState(null);
  const callContactRef = useRef(null);
  const activeCallRef = useRef(null);
  const incomingLookupIdRef = useRef(0);
  const [callPhase, setCallPhase] = useState('idle');
  const [isMuted, setIsMuted] = useState(false);
  const [isHeld, setIsHeld] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isConsulting, setIsConsulting] = useState(false);
  const [isTransferring, setIsTransferring] = useState(false);

  const [myExtension, setMyExtension] = useState(null);
  const [callContact, setCallContact] = useState(null);
  const [contactLookupDone, setContactLookupDone] = useState(true);
  const lookupIdRef = useRef(0);
  const [isRegistered, setIsRegistered] = useState(false);

  // 'unknown' | 'granted' | 'denied' | 'prompt'
  // When the browser has microphone permission turned off (denied), SIP can
  // still register but the audio track isn't available — calls don't work.
  // We surface this via context so a banner can prompt the user to fix it.
  const [microphonePermission, setMicrophonePermission] = useState('unknown');

  // Poll registration status every 5 seconds
  useEffect(() => {
    const checkRegistration = () => {
      const registered = engine.registerer?.state === 'Registered';
      setIsRegistered(registered);
    };
    checkRegistration();
    const interval = setInterval(checkRegistration, 5000);
    return () => clearInterval(interval);
  }, [initialized]);

  // Only run the microphone-permission probe for users who can actually place
  // calls — org has VoIP enabled AND the user has an extension assigned.
  // Sourced from the RTK members-with-extensions cache so it doesn't flicker
  // when group/role list payloads transiently overwrite currentUser.extensions.
  const { isVoipUser: isVoipEnabledForUser } = useIsVoipUser();

  useEffect(() => {
    if (!isVoipEnabledForUser) {
      setMicrophonePermission('unknown');
      return undefined;
    }

    let cancelled = false;
    let permissionStatus = null;

    const handleStatusChange = () => {
      if (!cancelled && permissionStatus) {
        setMicrophonePermission(permissionStatus.state);
      }
    };

    // Fallback for browsers (e.g. older Safari) that don't expose the
    // Permissions API for microphone — try to read device labels, which are
    // empty until the user has granted access.
    const fallbackProbe = async () => {
      if (!navigator.mediaDevices?.enumerateDevices) return;
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter((d) => d.kind === 'audioinput');
        if (cancelled) return;
        if (audioInputs.length === 0) {
          setMicrophonePermission('denied');
        } else if (audioInputs.every((d) => !d.label)) {
          setMicrophonePermission('prompt');
        } else {
          setMicrophonePermission('granted');
        }
      } catch (e) {
        // ignore
      }
    };

    (async () => {
      try {
        if (navigator.permissions?.query) {
          permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          if (cancelled) return;
          setMicrophonePermission(permissionStatus.state);
          permissionStatus.addEventListener('change', handleStatusChange);
          return;
        }
      } catch (e) {
        // browser doesn't support querying microphone permissions; fall through
      }
      fallbackProbe();
    })();

    // Re-probe via the fallback whenever the device list changes (covers the
    // case where the user toggles the mic permission in browser settings).
    const onDeviceChange = () => fallbackProbe();
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    }

    return () => {
      cancelled = true;
      if (permissionStatus) {
        permissionStatus.removeEventListener('change', handleStatusChange);
      }
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
      }
    };
  }, [isVoipEnabledForUser]);

  const [isSilentMode, setIsSilentMode] = useState(() => {
    try { return localStorage.getItem('voip_silent_mode') === 'true'; } catch (e) { return false; }
  });
  const [isDndMode, setIsDndMode] = useState(() => {
    try { return localStorage.getItem('voip_dnd_mode') === 'true'; } catch (e) { return false; }
  });

  // Keep engine in sync with silent/dnd mode state
  engine.silentMode = isSilentMode;
  engine.dndMode = isDndMode;

  const toggleSilentMode = useCallback(() => {
    setIsSilentMode((prev) => {
      const next = !prev;
      try { localStorage.setItem('voip_silent_mode', String(next)); } catch (e) { /* ignore */ }
      engine.silentMode = next;
      return next;
    });
  }, []);

  const toggleDndMode = useCallback(async () => {
    setIsDndMode((prev) => {
      const next = !prev;
      try { localStorage.setItem('voip_dnd_mode', String(next)); } catch (e) { /* ignore */ }
      engine.dndMode = next;
      if (next) {
        engine.unregister().catch(() => {});
      } else {
        engine.register().catch(() => {});
      }
      return next;
    });
  }, []);

  // Derive the SIP proxy from the tier's app URL so dev/staging/prod all
  // resolve to the right edge (sip.dev-rhinogram.com / sip.staging-rhinogram.com
  // / sip.rhinogram.com). REACT_APP_URL is the tier app domain (e.g.
  // "https://app.dev-rhinogram.com"); strip the leading subdomain label
  // ("app.") to get the bare tier domain, then prefix "sip.". Local dev
  // (localhost / 127.0.0.1) has no tier domain — fall back to the dev edge.
  const proxy = useMemo(() => {
    const { hostname } = new URL(process.env.REACT_APP_URL);
    if (!hostname.includes('.') || hostname === '127.0.0.1') {
      return 'sip.dev-rhinogram.com';
    }
    const baseDomain = hostname.replace(/^[^.]+\./, '');
    return `sip.${baseDomain}`;
  }, []);

  const lookupContact = useCallback(async (phoneNumber) => {
    if (!phoneNumber) {
      return; // Don't clear contact or invalidate an in-flight lookup
    }
    lookupIdRef.current += 1;
    const thisLookup = lookupIdRef.current;
    setContactLookupDone(false);
    const normalized = normalizePhone(phoneNumber);

    try {
      const response = await axios.get(`${process.env.REACT_APP_V3_BASE_URL}/voip/lookup/phone`, {
        params: { number: normalized },
      });
      if (thisLookup !== lookupIdRef.current) {
        return;
      }
      const contact = response.data?.userrecord || response.data?.user || null;
      setCallContact(contact);
    } catch (err) {
      if (thisLookup !== lookupIdRef.current) return;
      setCallContact(null);
    } finally {
      if (thisLookup === lookupIdRef.current) {
        setContactLookupDone(true);
      }
    }
  }, []);

  // Same lookup for a waiting caller, with its own in-flight guard so it neither cancels nor
  // overwrites the active call's contact.
  const lookupIncomingContact = useCallback(async (phoneNumber) => {
    if (!phoneNumber) return;
    incomingLookupIdRef.current += 1;
    const thisLookup = incomingLookupIdRef.current;
    try {
      const response = await axios.get(`${process.env.REACT_APP_V3_BASE_URL}/voip/lookup/phone`, {
        params: { number: normalizePhone(phoneNumber) },
      });
      if (thisLookup !== incomingLookupIdRef.current) return;
      setIncomingContact(response.data?.userrecord || response.data?.user || null);
    } catch (err) {
      if (thisLookup === incomingLookupIdRef.current) setIncomingContact(null);
    }
  }, []);

  useEffect(() => {
    activeCallRef.current = activeCall;
  }, [activeCall]);

  useEffect(() => {
    callContactRef.current = callContact;
  }, [callContact]);

  // Tracks which org's domain we last registered SIP against. Compared in
  // the org-change effect below to decide when to tear down + re-register.
  const initializedOrgIdRef = useRef(null);

  // Reusable initializer we can call from web OR mobile
  const initializeVoip = useCallback(
    async (options = {}) => {
      if (initialized) return;

      if (!organization?.isVoipEnabled) {
        console.warn('VOIP Not Enabled, skipping SIP setup'); // eslint-disable-line no-console
        return;
      }

      if (isMobile()) {
        console.warn('VOIP is desktop-only, skipping SIP setup on mobile'); // eslint-disable-line no-console
        return;
      }

      const domain = organization?.voipDomain;
      const extension = currentUser?.extensions?.[0]?.extension;
      const password = currentUser?.extensions?.[0]?.password;

      setMyExtension(extension);

      if (!extension || !password) {
        console.warn('Missing SIP credentials — skipping SIP init.'); // eslint-disable-line no-console
        return;
      }

      try {
        await engine.init({
          domain,
          extension,
          password,
          proxy,

          // tell sipClient whether we’re in the native shell
          isNativeApp: !!isNativeApp,

          // allow RN payloads to flow through later if needed
          ...options,
        });
        await engine.start();
        initializedOrgIdRef.current = organization?.id ?? null;
        setInitialized(true);
      } catch (err) {
        console.error('SIP init/start failed:', err);
      }
    },
    [initialized, organization, currentUser, proxy, isNativeApp],
  );

  const stopVoip = useCallback(async () => {
    try {
      await engine.stop();
    } catch (err) {
      console.error('SIP stop failed:', err);
    } finally {
      setInitialized(false);
      setClientReady(false);
      setIncomingCall(null);
      setActiveCall(null);
      setCallPhase('idle');
      setIsMuted(false);
      setIsHeld(false);
      setIsConsulting(false);
      setIsTransferring(false);
    }
  }, []);

  // When the user switches orgs (multi-org accounts), tear down the SIP
  // session so the next init cycle picks up the new org's voipDomain.
  // Without this the engine stays registered against whichever org was
  // first initialized, even though `getCurrentOrg` now points elsewhere.
  useEffect(() => {
    const targetOrgId = organization?.id ?? null;
    if (!initialized) return;
    if (initializedOrgIdRef.current == null) return;
    if (initializedOrgIdRef.current === targetOrgId) return;
    stopVoip().catch(() => {});
    initializedOrgIdRef.current = null;
  }, [organization?.id, initialized, stopVoip]);

  useEffect(() => {
    let mounted = true;

    if (!engine.fetchTurnCredentialsFn) {
      engine.fetchTurnCredentialsFn = async () =>
        getTurnServers().unwrap();
    }

    const onReady = () => {
      if (!mounted) return;
      setClientReady(true);
    };

    const onIncomingCall = (call) => {
      if (!mounted) return;
      const callerNumber = call?.remoteIdentity?.uri?.user || '';
      // Second call while one is up: show it as call waiting. The active call keeps its phase,
      // its panel and its contact; only the incoming slot changes.
      if (activeCallRef.current) {
        ReactDOM.unstable_batchedUpdates(() => {
          setIncomingCall(call);
          setIncomingContact(null);
        });
        if (callerNumber) lookupIncomingContact(callerNumber);
        return;
      }
      ReactDOM.unstable_batchedUpdates(() => {
        setIncomingCall(call);
        setCallPhase('ringing');
        setContactLookupDone(false);
        setCallContact(null);
      });
      if (callerNumber) {
        lookupContact(callerNumber);
      }
    };

    const onOutgoingCall = (call, target) => {
      if (!mounted) return;
      if (target && !call._outgoingTarget) {
        call._outgoingTarget = target; // eslint-disable-line no-param-reassign
      }
      ReactDOM.unstable_batchedUpdates(() => {
        setActiveCall(call);
        setCallPhase('active');
      });
      lookupContact(target);
    };

    const onCallConnected = (call) => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setActiveCall(call);
        setCallPhase('active');
        setIsHeld(!!call?._isHeld);
        setIsMuted(!!call?._isMuted);
        setIncomingContact(null);
      });
      setTimeout(() => mounted && setIncomingCall(null), 0);
      // Look the contact up for whichever call just became active (a newly answered call or a
      // parked call being resumed) so the panel always shows the person actually on the line.
      const number = call?._outgoingTarget || call?.remoteIdentity?.uri?.user || '';
      if (number) lookupContact(number);
    };

    const onCallParked = (call) => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setHeldCall(call);
        setHeldContact(callContactRef.current);
      });
    };

    // Swap: the call being parked keeps the contact the panel was showing; callConnected for the
    // other leg (emitted right after) looks up the contact now on the line.
    const onCallSwapped = ({ held } = {}) => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setHeldCall(held || null);
        setHeldContact(callContactRef.current);
      });
    };

    const onCallResumed = () => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setHeldCall(null);
        setHeldContact(null);
      });
    };

    const onHeldCallEnded = () => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setHeldCall(null);
        setHeldContact(null);
      });
    };

    // A waiting call stopped ringing (caller hung up or it rolled to voicemail) while another
    // call is in progress: drop the banner, leave the active call alone.
    const onIncomingCallEnded = () => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setIncomingCall(null);
        setIncomingContact(null);
      });
    };

    const onCallProgress = (call) => {
      if (!mounted) return;
      setActiveCall({ ...call });
    };

    const onCallRejected = (payload) => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setIncomingCall(null);
        setIncomingContact(null);
        // Declining a waiting call ends nothing else: the call in progress stays 'active'.
        if (!payload?.hasActiveCall) setCallPhase('ended');
      });
    };

    const onCallEnded = () => {
      if (!mounted) return;
      ReactDOM.unstable_batchedUpdates(() => {
        setActiveCall(null);
        setIncomingCall(null);
        setHeldCall(null);
        setHeldContact(null);
        setCallPhase('ended');
        setIsTransferring(false);
        setCallContact(null);
        setIncomingContact(null);
        setIsMuted(false);
        setIsHeld(false);
      });
    };

    const onMuteToggled = (val) => {
      if (!mounted) return;
      setIsMuted(val === true);
    };

    const onHoldToggled = (val) => {
      if (!mounted) return;
      setIsHeld(val === true);
    };

    // CONSULT / TRANSFER
    const onConsultStarted = () => setIsConsulting(true);
    const onConsultEnded = () => setIsConsulting(false);
    const onConsultCancelled = () => setIsConsulting(false);

    const onTransferStarted = (t) => {
      if (!mounted) return;
      if (t?.type === 'blind') setIsTransferring(true);
      if (t?.type === 'consult') setIsTransferring(false);
    };

    const onTransferError = () => {
      if (!mounted) return;
      setIsTransferring(false);
    };

    // REGISTER LISTENERS
    engine.on('ready', onReady);
    engine.on('incomingCall', onIncomingCall);
    engine.on('outgoingCall', onOutgoingCall);
    engine.on('callConnected', onCallConnected);
    engine.on('callProgress', onCallProgress);
    engine.on('callRejected', onCallRejected);
    engine.on('callEnded', onCallEnded);
    engine.on('callParked', onCallParked);
    engine.on('callSwapped', onCallSwapped);
    engine.on('callResumed', onCallResumed);
    engine.on('heldCallEnded', onHeldCallEnded);
    engine.on('incomingCallEnded', onIncomingCallEnded);

    engine.on('muteToggled', onMuteToggled);
    engine.on('holdToggled', onHoldToggled);

    engine.on('consultStarted', onConsultStarted);
    engine.on('consultEnded', onConsultEnded);
    engine.on('consultCancelled', onConsultCancelled);
    engine.on('transferStarted', onTransferStarted);
    engine.on('transferError', onTransferError);

    // STOP SIP ON TAB/REFRESH
    const handleUnload = () => {
      stopVoip().catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);

    initializeVoip().catch(() => {});

    return () => {
      mounted = false;
      engine.off('ready', onReady);
      engine.off('incomingCall', onIncomingCall);
      engine.off('outgoingCall', onOutgoingCall);
      engine.off('callConnected', onCallConnected);
      engine.off('callProgress', onCallProgress);
      engine.off('callRejected', onCallRejected);
      engine.off('callEnded', onCallEnded);
      engine.off('callParked', onCallParked);
      engine.off('callSwapped', onCallSwapped);
      engine.off('callResumed', onCallResumed);
      engine.off('heldCallEnded', onHeldCallEnded);
      engine.off('incomingCallEnded', onIncomingCallEnded);

      engine.off('muteToggled', onMuteToggled);
      engine.off('holdToggled', onHoldToggled);

      engine.off('consultStarted', onConsultStarted);
      engine.off('consultEnded', onConsultEnded);
      engine.off('consultCancelled', onConsultCancelled);
      engine.off('transferStarted', onTransferStarted);
      engine.off('transferError', onTransferError);

      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [initializeVoip, stopVoip, lookupContact, lookupIncomingContact]);

  const value = {
    clientReady,
    isRegistered,
    callPhase,
    incomingCall,
    activeCall,
    heldCall,
    heldContact,
    incomingContact,
    // True while a second call rings during an established call.
    isCallWaiting: !!incomingCall && !!activeCall,
    swapCalls: () => engine.swapCalls?.(),
    isMuted,
    isHeld,
    microphonePermission,
    isConsulting,
    isTransferring,
    myExtension,
    isSilentMode,
    toggleSilentMode,
    isDndMode,
    toggleDndMode,
    callContact,
    contactLookupDone,
    dialedNumber,
    setDialedNumber,
    initializeVoip,
    call: (target) => {
      setDialedNumber(target);
      lookupContact(target);
      engine.call(target);
    },
    answer: () => {
      engine.answer();
    },
    reject: () => {
      engine.reject();
    },
    hangup: () => {
      engine.hangup();
    },
    toggleMute: () => engine.toggleMute(),
    toggleHold: () => engine.toggleHold(),
    sendDTMF: (tone) => engine.sendDTMF(tone),
    blindTransfer: (target) => engine.blindTransfer(target),
    startConsultTransfer: (target) => engine.startConsultTransfer(target),
    completeConsultTransfer: () => engine.completeConsultTransfer(),
    cancelConsultTransfer: () => engine.cancelConsultTransfer(),

    stopVoip,
  };

  return <VoipContext.Provider value={value}>{children}</VoipContext.Provider>;
};
